import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2, Mail } from 'lucide-react'
import { organizationApi } from '@/services/organization-api'
import { useOrgStore } from '@/stores/org-store'
import { useAuthStore } from '@/stores/auth-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'

export default function InviteTokenStep() {
  const [token, setToken] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const navigate = useNavigate()
  const fetchOrgs = useOrgStore((s) => s.fetchOrgs)
  const setOnboardingCompleted = useAuthStore((s) => s.setOnboardingCompleted)

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await organizationApi.acceptInvite(token.trim())
      await fetchOrgs()
      setOnboardingCompleted(true)
      navigate('/app/dashboard')
    } catch {
      setError('Invalid or expired invite token')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">Join via Invite Token</h2>
        <p className="text-muted-foreground mt-1">Enter the invite token you received from your team</p>
      </div>

      <div className="flex items-center justify-center py-6">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <Mail className="h-8 w-8 text-primary" />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-2">
        <Label htmlFor="token">Invite Token</Label>
        <Input
          id="token"
          placeholder="Paste your invite token here"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          required
          disabled={isSubmitting}
        />
      </div>

      <Button type="submit" disabled={isSubmitting} className="w-full">
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Joining...
          </>
        ) : (
          'Join Organization'
        )}
      </Button>
    </form>
  )
}
